// Permission catalog. UI gating reads through Clerk's `useAuth().has()`
// (see useCapabilities.ts) against these canonical slugs — never role
// string matching.
//
// Slug shape — important: Clerk's session token v2 only carries CUSTOM
// org permissions in the JWT (claims.fea + claims.o.per + claims.o.fpm).
// Built-in `org:sys_*:manage` permissions exist in Clerk's data model but
// are NEVER emitted in the JWT, so `has({ permission: 'org:sys_*' })`
// always returns false. Every entry below MUST be a custom permission
// defined in the Clerk dashboard / via `clerk api`.
//
// Source of truth lives in the Clerk instance. Adding a new gate:
//   1. Create a custom permission (non-sys namespace) for both dev + prod:
//      `clerk api -X POST /v1/organization_permissions -d '{...}'`.
//   2. Attach to the appropriate role(s) via
//      `/v1/organization_roles/{role_id}/permissions/{perm_id}`.
//   3. Verify it appears in a freshly minted JWT (claims.o.per).
//   4. Add the slug here and to apps/platform/src/common/auth/permissions.ts,
//      then surface a capability in useCapabilities.

export type OrgRole = string;

export const PERMISSIONS = {
  billingManage: "org:billing:manage_subscription",
  // Custom permission — viewing the workspace audit log. Owner-only by
  // default; attach to other roles in Clerk dashboard if needed.
  auditLogRead: "org:audit_log:read",
  // Custom permissions, owner-only by default. These replace the Clerk
  // built-in `org:sys_*` slugs, which v2 session tokens never emit —
  // `has({ permission: 'org:sys_*' })` always returns false. Defined in
  // Clerk for dev + prod and attached to the owner role.
  membershipsManage: "org:memberships:manage",
  profileManage: "org:profile:manage",
  profileDelete: "org:profile:delete",
} as const;

export type PermissionSlug = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
